import React, { useEffect, useMemo, useState, useRef } from 'react';
import { XIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { PTPData, HierarchyControls } from '../types/ptp';
interface PTPExportViewProps {
  data: PTPData;
  onClose: () => void;
  isUpdated?: boolean;
}
const A4_WIDTH = 794;
const A4_HEIGHT = 1123;
export function PTPExportView({
  data,
  onClose,
  isUpdated = false
}: PTPExportViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [pageHeight, setPageHeight] = useState(A4_HEIGHT);
  useEffect(() => {
    const updateScale = () => {
      if (!containerRef.current) return;
      const {
        clientWidth
      } = containerRef.current;
      const padding = 40;
      const availW = clientWidth - padding * 2;
      // Scale based on width only — let height scroll naturally
      setScale(Math.min(availW / A4_WIDTH, 1));
    };
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, []);
  // Track actual page content height for the scroll wrapper
  useEffect(() => {
    if (!pageRef.current) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setPageHeight(entry.contentRect.height);
      }
    });
    observer.observe(pageRef.current);
    return () => observer.disconnect();
  }, []);
  const formatControls = (controls: HierarchyControls | string[] | any) => {
    if (!controls) return 'N/A';
    if (Array.isArray(controls)) return controls.join('; ');
    const parts: string[] = [];
    if (controls.eliminateSubstitute && controls.eliminateSubstitute !== 'N/A') parts.push(controls.eliminateSubstitute);
    if (controls.engineering && controls.engineering !== 'N/A') parts.push(controls.engineering);
    if (controls.adminTraining && controls.adminTraining !== 'N/A') parts.push(controls.adminTraining);
    if (controls.ppe && controls.ppe !== 'N/A') parts.push(controls.ppe);
    return parts.length > 0 ? parts.join('; ') : 'N/A';
  };
  // Collect all unique equipment from step hazards
  const allEquipment = useMemo(() => {
    const set = new Set<string>();
    data.stepHazardData.forEach(step => step.hazards.forEach(h => h.equipment.forEach(e => set.add(e))));
    data.generalEquipment.forEach(e => set.add(e));
    data.equipment.forEach(e => set.add(e));
    return Array.from(set);
  }, [data]);
  // Separate equipment into categories
  const mechanicalAids = useMemo(() => {
    const keywords = ['crane', 'forklift', 'telehandler', 'hoist', 'winch', 'jack'];
    return allEquipment.filter(e => keywords.some(k => e.toLowerCase().includes(k)));
  }, [allEquipment]);
  const handTools = useMemo(() => {
    return allEquipment.filter(e => !mechanicalAids.includes(e) && !['Barricades', 'Signage', 'PPE', 'Radios'].includes(e));
  }, [allEquipment, mechanicalAids]);
  const neededPermits = (data.permits || []).filter(p => p.needed);
  // Use stepHazardData as the primary source (it has real data)
  const stepsToRender = data.stepHazardData;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="absolute inset-0 z-[100] flex flex-col bg-[#F8F9FA] rounded-lg overflow-hidden" data-id="element-8">
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-neutral-200 flex-shrink-0 shadow-sm" data-id="element-9">
        <h2 className="text-2xl font-black text-[#0B192C] tracking-tight" data-id="element-10">
          Preview Pre-Shift
        </h2>
        <button onClick={onClose} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md hover:bg-neutral-100 text-neutral-600 hover:text-neutral-900 transition-colors text-sm font-medium" data-id="element-11">
          <XIcon className="w-4 h-4" data-id="element-12" />
          Close
        </button>
      </div>

      <div ref={containerRef} className="flex-1 overflow-auto flex justify-center p-4" data-id="element-13">
        {/* Wrapper div sets explicit scaled dimensions so scroll works correctly */}
        <div style={{
        width: A4_WIDTH * scale,
        height: pageHeight * scale,
        flexShrink: 0
      }} data-id="element-14">
          <div ref={pageRef} style={{
          width: A4_WIDTH,
          minHeight: A4_HEIGHT,
          transform: `scale(${scale})`,
          transformOrigin: 'top left'
        }} className="bg-white shadow-xl relative" data-id="element-15">
            <div className="absolute top-0 right-0 bottom-0 w-3 bg-[#B0B0B0] rounded-l-md opacity-50" data-id="element-16" />

            <div className="p-8 text-black" style={{
            fontFamily: 'Inter, Arial, sans-serif',
            fontSize: '9px',
            lineHeight: '1.4'
          }} data-id="element-17">
              {/* Header Section */}
              <div className="flex justify-between items-start mb-3" data-id="element-18">
                <div className="border-l-2 border-black pl-3" data-id="element-19">
                  <div className="font-medium text-[11px]" data-id="element-20">Your Company</div>
                  <div className="font-bold text-[13px]" data-id="element-21">
                    {data.project || 'Pratt Student Health & Wellness Center'}
                  </div>
                </div>
                <div className="text-right pr-4" data-id="element-22">
                  <div className="text-[9px] font-medium text-neutral-600 mb-0.5" data-id="element-23">
                    Powered by
                  </div>
                  <div className="flex items-center justify-end text-base font-black tracking-tighter" data-id="element-24">
                    Bilt
                    <span className="relative inline-flex items-center justify-center w-3.5 h-3.5 mx-0.5" data-id="element-25">
                      <span className="absolute inset-0 bg-[#FFC107] rounded-full" data-id="element-26"></span>
                      <span className="relative text-black text-[8px] z-10" data-id="element-27">
                        n
                      </span>
                    </span>
                  </div>
                </div>
              </div>

              <div className="font-bold text-[10px] mb-5 pb-3 border-b border-neutral-200" data-id="element-28">
                Project Number: ######## • Site Address: {data.location}
              </div>

              {/* Title Section */}
              <div className="flex items-center gap-2 mb-4" data-id="element-29">
                <div className="w-1 h-6 bg-[#FFC107]" data-id="element-30"></div>
                <h1 className="text-[20px] font-black text-[#1A1A1A] tracking-tight" data-id="element-31">
                  Pre-Shift / Pre-Task Plan
                </h1>
              </div>

              {/* Info Grid */}
              <div className="space-y-1.5 mb-5 text-[10px]" data-id="element-32">
                <div data-id="element-33">
                  <span className="font-bold" data-id="element-34">Date:</span> {data.date}
                </div>
                <div data-id="element-35">
                  <span className="font-bold" data-id="element-36">Company:</span> Your Company
                  <span className="mx-1.5" data-id="element-37">•</span>
                  <span className="font-bold" data-id="element-38">Trade:</span> Steel Erection
                </div>
                <div data-id="element-39">
                  <span className="font-bold" data-id="element-40">Location of Task:</span>{' '}
                  {data.location}
                </div>
                <div data-id="element-41">
                  <span className="font-bold" data-id="element-42">Description of Activity:</span>{' '}
                  {data.taskTitle || 'Pre-Task Plan'}
                </div>
                <div data-id="element-43">
                  <span className="font-bold" data-id="element-44">Crew Size:</span> {data.crewSize}{' '}
                  workers
                </div>
              </div>

              {/* Safety and Health Consideration */}
              <h3 className="font-bold text-[12px] mb-2" data-id="element-45">
                Safety and Health Consideration
              </h3>
              <div className="grid grid-cols-3 gap-3 mb-5" data-id="element-46">
                {/* Hierarchy of Control */}
                <div className="border border-neutral-300 rounded-lg p-3 flex flex-col" data-id="element-47">
                  <h4 className="font-bold text-[11px] text-center mb-3" data-id="element-48">
                    Hierarchy of Control
                  </h4>
                  <div className="flex justify-between items-center flex-1" data-id="element-49">
                    <div className="text-[8px] font-bold space-y-1.5" data-id="element-50">
                      <div data-id="element-51">1. Elimination</div>
                      <div data-id="element-52">2. Substitution</div>
                      <div data-id="element-53">3. Engineering</div>
                      <div data-id="element-54">4. Isolation</div>
                      <div data-id="element-55">5. Administration</div>
                      <div data-id="element-56">6. Personal Protective Equip</div>
                    </div>
                    <div className="w-16 h-16 bg-neutral-100 border border-neutral-200 p-0.5" data-id="element-57">
                      <div className="w-full h-full grid grid-cols-5 grid-rows-5 gap-px" data-id="element-58">
                        {Array.from({
                        length: 25
                      }).map((_, i) => <div key={i} className={[0, 1, 2, 3, 4, 5, 9, 10, 14, 15, 19, 20, 21, 22, 23, 24, 6, 8, 16, 18, 12].includes(i) ? 'bg-black' : 'bg-transparent'} data-id="element-59"></div>)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* System Approach */}
                <div className="border border-neutral-300 rounded-lg p-3 flex flex-col relative overflow-hidden" data-id="element-60">
                  <h4 className="font-bold text-[11px] text-center mb-3" data-id="element-61">
                    System Approach
                  </h4>
                  <div className="flex items-center justify-center flex-1 relative h-24" data-id="element-62">
                    <div className="absolute left-0 top-1 w-24 text-[7px] font-bold leading-tight z-10" data-id="element-63">
                      <div className="mb-1.5" data-id="element-64">
                        Environment (E):
                        <br data-id="element-65" />
                        <span className="font-normal" data-id="element-66">
                          Engineering Controls, Working Conditions
                        </span>
                      </div>
                      <div className="mb-1.5" data-id="element-67">
                        Capability (C):
                        <br data-id="element-68" />
                        <span className="font-normal" data-id="element-69">
                          Training, Education, Physical Capabilities
                        </span>
                      </div>
                      <div data-id="element-70">
                        Motivation (M):
                        <br data-id="element-71" />
                        <span className="font-normal" data-id="element-72">
                          Worker Actions, Factors driving motivation
                        </span>
                      </div>
                    </div>
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-20 h-20" data-id="element-73">
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-12 rounded-full border border-black bg-[#E8F5E9] opacity-80 flex items-start justify-center pt-1 text-[5px] font-bold" data-id="element-74">
                        Environment
                      </div>
                      <div className="absolute bottom-0 left-0 w-12 h-12 rounded-full border border-black bg-[#E3F2FD] opacity-80 flex items-center justify-start pl-1 text-[5px] font-bold" data-id="element-75">
                        Capability
                      </div>
                      <div className="absolute bottom-0 right-0 w-12 h-12 rounded-full border border-black bg-[#E0F7FA] opacity-80 flex items-center justify-end pr-1 text-[5px] font-bold" data-id="element-76">
                        Motivation
                      </div>
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-[#FFC107] border border-black z-10 flex items-center justify-center text-[3px] font-bold text-center leading-tight" data-id="element-77">
                        Safe
                        <br data-id="element-78" />
                        Work
                      </div>
                    </div>
                  </div>
                </div>

                {/* Permits */}
                <div className="border border-neutral-300 rounded-lg p-3 flex flex-col" data-id="element-79">
                  <h4 className="font-bold text-[11px] text-center mb-3" data-id="element-80">
                    Permits
                  </h4>
                  <div className="flex-1" data-id="element-81">
                    <div className="flex justify-between text-[8px] font-bold mb-1.5" data-id="element-82">
                      <span data-id="element-83">Permit Type</span>
                      <span data-id="element-84">Required</span>
                    </div>
                    <div className="space-y-1.5 text-[8px]" data-id="element-85">
                      {(data.permits && data.permits.length > 0 ? data.permits : [{
                      name: 'Hot Work',
                      needed: false,
                      exists: null
                    }, {
                      name: 'Energized Systems/LOTO',
                      needed: false,
                      exists: null
                    }, {
                      name: 'Ladder Last Permit',
                      needed: false,
                      exists: null
                    }, {
                      name: 'Ground Penetration',
                      needed: false,
                      exists: null
                    }, {
                      name: 'Confined Space',
                      needed: false,
                      exists: null
                    }, {
                      name: 'Roof Access',
                      needed: false,
                      exists: null
                    }]).map((permit, i) => <div key={i} className="flex justify-between items-center" data-id="element-86">
                          <span className="w-28 leading-tight" data-id="element-87">
                            {permit.name}
                          </span>
                          <div className="flex items-center gap-2" data-id="element-88">
                            <label className="flex items-center gap-0.5" data-id="element-89">
                              <div className={`w-2.5 h-2.5 border border-neutral-300 rounded-sm ${!permit.needed ? 'bg-[#FFC107] flex items-center justify-center' : 'bg-white'}`} data-id="element-90">
                                {!permit.needed && <svg className="w-1.5 h-1.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={4} data-id="element-91">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-92" />
                                  </svg>}
                              </div>
                              <span data-id="element-93">No</span>
                            </label>
                            <label className="flex items-center gap-0.5" data-id="element-94">
                              <div className={`w-2.5 h-2.5 border rounded-sm ${permit.needed ? permit.exists === false ? 'border-red-400 bg-red-500 flex items-center justify-center' : 'border-neutral-300 bg-[#FFC107] flex items-center justify-center' : 'border-neutral-300 bg-white'}`} data-id="element-95">
                                {permit.needed && <svg className="w-1.5 h-1.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={4} data-id="element-96">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-97" />
                                  </svg>}
                              </div>
                              <span data-id="element-98">Yes</span>
                            </label>
                          </div>
                        </div>)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Steps - Hazards - Eliminate/Control */}
              <h3 className="font-bold text-[12px] mb-1.5" data-id="element-99">
                Steps - Hazards - Eliminate/Control
              </h3>
              <table className="w-full border-collapse mb-5 text-[8px]" data-id="element-100">
                <thead data-id="element-101">
                  <tr className="bg-[#555555] text-white" data-id="element-102">
                    <th className="border border-white px-1.5 py-1.5 text-left font-bold w-[30px]" data-id="element-103">
                      Step
                    </th>
                    <th className="border border-white px-1.5 py-1.5 text-left font-bold w-[22%]" data-id="element-104">
                      Method of Procedure (MOP)
                    </th>
                    <th className="border border-white px-1.5 py-1.5 text-left font-bold w-[22%]" data-id="element-105">
                      Hazards Associated With Each Steps
                    </th>
                    <th className="border border-white px-1.5 py-1.5 text-left font-bold w-[30px]" data-id="element-106">
                      ECM
                    </th>
                    <th className="border border-white px-1.5 py-1.5 text-left font-bold" data-id="element-107">
                      Eliminate Or Control The Hazard
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-108">
                  {stepsToRender.map((step, i) => {
                  const relevantHazards = step.hazards.filter(h => h.relevant);
                  if (relevantHazards.length === 0) {
                    return <tr key={i} className="border-b border-neutral-300" data-id="element-109">
                          <td className="border-r border-neutral-300 px-1.5 py-1.5 align-top text-center font-bold" data-id="element-110">
                            {i + 1}
                          </td>
                          <td className="border-r border-neutral-300 px-1.5 py-1.5 align-top" data-id="element-111">
                            {step.description}
                          </td>
                          <td className="border-r border-neutral-300 px-1.5 py-1.5 align-top text-neutral-400 italic" data-id="element-112">
                            None identified
                          </td>
                          <td className="border-r border-neutral-300 px-1.5 py-1.5 align-top text-center" data-id="element-113">
                            —
                          </td>
                          <td className="px-1.5 py-1.5 align-top" data-id="element-114">N/A</td>
                        </tr>;
                  }
                  return relevantHazards.map((h, j) => <tr key={`${i}-${j}`} className="border-b border-neutral-300" data-id="element-115">
                        {j === 0 && <>
                            <td className="border-r border-neutral-300 px-1.5 py-1.5 align-top text-center font-bold" rowSpan={relevantHazards.length} data-id="element-116">
                              {i + 1}
                            </td>
                            <td className="border-r border-neutral-300 px-1.5 py-1.5 align-top" rowSpan={relevantHazards.length} data-id="element-117">
                              {step.description}
                            </td>
                          </>}
                        <td className="border-r border-neutral-300 px-1.5 py-1.5 align-top" data-id="element-118">
                          {h.hazard}
                        </td>
                        <td className="border-r border-neutral-300 px-1.5 py-1.5 align-top text-center font-bold" data-id="element-119">
                          {h.ecmDriver}
                        </td>
                        <td className="px-1.5 py-1.5 align-top" data-id="element-120">
                          {formatControls(h.controls)}
                        </td>
                      </tr>);
                })}
                </tbody>
              </table>

              {/* General Hazards */}
              {data.generalHazards.length > 0 && <>
                  <h3 className="font-bold text-[12px] mb-1.5" data-id="element-121">
                    General Site Hazards
                  </h3>
                  <table className="w-full border-collapse mb-5 text-[8px]" data-id="element-122">
                    <thead data-id="element-123">
                      <tr className="bg-[#555555] text-white" data-id="element-124">
                        <th className="border border-white px-1.5 py-1.5 text-left font-bold w-[25%]" data-id="element-125">
                          Hazard
                        </th>
                        <th className="border border-white px-1.5 py-1.5 text-left font-bold w-[25%]" data-id="element-126">
                          Source
                        </th>
                        <th className="border border-white px-1.5 py-1.5 text-left font-bold w-[30px]" data-id="element-127">
                          ECM
                        </th>
                        <th className="border border-white px-1.5 py-1.5 text-left font-bold" data-id="element-128">
                          Controls
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-129">
                      {data.generalHazards.filter(h => h.relevant).map((h, i) => <tr key={i} className="border-b border-neutral-300" data-id="element-130">
                            <td className="border-r border-neutral-300 px-1.5 py-1.5 align-top font-medium" data-id="element-131">
                              {h.hazard}
                            </td>
                            <td className="border-r border-neutral-300 px-1.5 py-1.5 align-top" data-id="element-132">
                              {h.source}
                            </td>
                            <td className="border-r border-neutral-300 px-1.5 py-1.5 align-top text-center font-bold" data-id="element-133">
                              {h.ecmDriver}
                            </td>
                            <td className="px-1.5 py-1.5 align-top" data-id="element-134">
                              {formatControls(h.controls)}
                            </td>
                          </tr>)}
                    </tbody>
                  </table>
                </>}

              {/* Crew Assignment */}
              <h3 className="font-bold text-[12px] mb-1.5" data-id="element-135">
                Crew Assignment ({data.crewSize} Workers)
              </h3>
              <table className="w-full border-collapse mb-5 text-[8px]" data-id="element-136">
                <thead data-id="element-137">
                  <tr className="bg-[#555555] text-white" data-id="element-138">
                    <th className="border border-white px-1.5 py-1.5 text-left font-bold w-[20px]" data-id="element-139">
                      #
                    </th>
                    <th className="border border-white px-1.5 py-1.5 text-left font-bold w-[25%]" data-id="element-140">
                      Name
                    </th>
                    <th className="border border-white px-1.5 py-1.5 text-left font-bold w-[20%]" data-id="element-141">
                      Role
                    </th>
                    <th className="border border-white px-1.5 py-1.5 text-left font-bold" data-id="element-142">
                      Responsibility
                    </th>
                    <th className="border border-white px-1.5 py-1.5 text-left font-bold w-[80px]" data-id="element-143">
                      Signature
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-144">
                  {data.crewRoles.map((role, i) => <tr key={i} className="border-b border-neutral-300" data-id="element-145">
                      <td className="border-r border-neutral-300 px-1.5 py-2 text-center" data-id="element-146">
                        {i + 1}
                      </td>
                      <td className="border-r border-neutral-300 px-1.5 py-2 font-medium" data-id="element-147">
                        {role.name}
                      </td>
                      <td className="border-r border-neutral-300 px-1.5 py-2" data-id="element-148">
                        {role.role}
                      </td>
                      <td className="border-r border-neutral-300 px-1.5 py-2" data-id="element-149">
                        {role.responsibility}
                      </td>
                      <td className="px-1.5 py-2" data-id="element-150"></td>
                    </tr>)}
                </tbody>
              </table>

              {/* Meeting Additions */}
              {data.meetingAdditions && data.meetingAdditions.length > 0 && <>
                  <h3 className="font-bold text-[12px] mb-1.5 text-[#d97706]" data-id="element-151">
                    Meeting Additions (Identified During Briefing)
                  </h3>
                  <table className="w-full border-collapse mb-5 text-[8px]" data-id="element-152">
                    <thead data-id="element-153">
                      <tr className="bg-[#d97706] text-white" data-id="element-154">
                        <th className="border border-white px-1.5 py-1.5 text-left font-bold w-[15%]" data-id="element-155">
                          Type
                        </th>
                        <th className="border border-white px-1.5 py-1.5 text-left font-bold w-[25%]" data-id="element-156">
                          Step Reference
                        </th>
                        <th className="border border-white px-1.5 py-1.5 text-left font-bold w-[35%]" data-id="element-157">
                          Addition
                        </th>
                        <th className="border border-white px-1.5 py-1.5 text-left font-bold w-[25%]" data-id="element-158">
                          Source / Reason
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-159">
                      {data.meetingAdditions.map((item, i) => <tr key={i} className="border-b border-neutral-300" data-id="element-160">
                          <td className="border-r border-neutral-300 px-1.5 py-1.5 align-top font-bold uppercase" data-id="element-161">
                            {item.type}
                          </td>
                          <td className="border-r border-neutral-300 px-1.5 py-1.5 align-top" data-id="element-162">
                            {item.stepRef}
                          </td>
                          <td className="border-r border-neutral-300 px-1.5 py-1.5 align-top font-medium" data-id="element-163">
                            {item.title}
                          </td>
                          <td className="px-1.5 py-1.5 align-top italic" data-id="element-164">
                            "{item.reason}" — {item.source}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </>}

              {/* Materials / Tools / Others */}
              <h3 className="font-bold text-[12px] mb-1.5" data-id="element-165">
                Materials / Tools / Others
              </h3>
              <table className="w-full border-collapse mb-5 text-[8px]" data-id="element-166">
                <thead data-id="element-167">
                  <tr className="bg-[#555555] text-white" data-id="element-168">
                    <th className="border border-white px-1.5 py-1.5 text-left font-bold w-[33%]" data-id="element-169">
                      Materials, Tools & Equipment
                    </th>
                    <th className="border border-white px-1.5 py-1.5 text-left font-bold w-[33%]" data-id="element-170">
                      Mechanical Aids
                    </th>
                    <th className="border border-white px-1.5 py-1.5 text-left font-bold w-[33%]" data-id="element-171">
                      PPE & Safety Controls
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-172">
                  <tr className="border-b border-neutral-300" data-id="element-173">
                    <td className="border-r border-neutral-300 px-1.5 py-2 align-top" data-id="element-174">
                      {handTools.length > 0 ? handTools.join(', ') : 'Standard hand tools'}
                    </td>
                    <td className="border-r border-neutral-300 px-1.5 py-2 align-top" data-id="element-175">
                      {mechanicalAids.length > 0 ? mechanicalAids.join(', ') : 'N/A'}
                    </td>
                    <td className="px-1.5 py-2 align-top" data-id="element-176">
                      {data.ppeControls.length > 0 ? data.ppeControls.join(', ') : 'Hard hat, Safety glasses, Steel toe boots, High-vis vest'}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Environment Conditions */}
              {data.environmentConditions.length > 0 && <>
                  <h3 className="font-bold text-[12px] mb-1.5" data-id="element-177">
                    Environment Conditions
                  </h3>
                  <div className="border border-neutral-300 rounded px-2 py-1.5 mb-5 text-[8px] flex flex-wrap gap-x-4 gap-y-1" data-id="element-178">
                    {data.environmentConditions.map((cond, i) => <span key={i} className="flex items-center gap-1" data-id="element-179">
                        <span className="inline-block w-2 h-2 border border-neutral-500 bg-neutral-100 text-center text-[5px] leading-[8px] font-bold" data-id="element-180">
                          ✓
                        </span>
                        {cond}
                      </span>)}
                  </div>
                </>}

              {/* Supervisor signature */}
              <h3 className="font-bold text-[12px] mb-1.5" data-id="element-181">
                Supervisor Signature
              </h3>
              <div className="flex gap-4" data-id="element-182">
                <div className="bg-[#F8F9FA] rounded-lg p-3 w-48 border border-neutral-200" data-id="element-183">
                  <div className="h-12 flex items-center justify-center mb-1.5" data-id="element-184">
                    {data.foremanSignature ? <img src={data.foremanSignature} alt="Foreman Signature" className="h-full object-contain invert" data-id="element-185" /> : <svg viewBox="0 0 200 60" className="w-28 h-10 opacity-80" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-186">
                        <path d="M20,40 C30,20 50,10 70,30 C90,50 110,20 130,10 C150,0 170,30 180,50" data-id="element-187" />
                        <path d="M60,10 C70,30 80,50 90,40" data-id="element-188" />
                      </svg>}
                  </div>
                  <div className="text-[8px] text-neutral-600 text-center border-t border-neutral-300 pt-1" data-id="element-189">
                    Submitted by:{' '}
                    {data.crewRoles.find(r => r.role === 'Foreman')?.name || 'Foreman'}
                  </div>
                </div>
                <div className="flex-1 text-[8px] text-neutral-500 space-y-2 pt-1" data-id="element-190">
                  <div data-id="element-191">Date / Time: ____________________</div>
                  <div data-id="element-192">Title: ____________________________</div>
                  <div className="text-[7px] italic mt-3" data-id="element-193">
                    By signing, the supervisor confirms this Pre-Task Plan has
                    been reviewed and all crew members have been briefed on the
                    hazards and controls.
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="border-t border-neutral-300 pt-2 mt-5 flex justify-between text-[7px] text-neutral-400" data-id="element-194">
                <span data-id="element-195">
                  PTP-2026-00847 |{' '}
                  {isUpdated ? 'Rev. 1 (AI Updated)' : 'Rev. 0'}
                </span>
                <span data-id="element-196">
                  CONFIDENTIAL — For authorized project personnel only
                </span>
                <span data-id="element-197">Generated: {data.date}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>;
}